import React, { useCallback, useState } from "react";
import { Button, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, styled } from "@mui/material";
import { getIcon } from "utils/iconsConfig";
import { useOrdersDetails } from "../apis/get-order-detail";
import ImageModal from "components/ui/ImageModal";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import apiClient from "lib/api-client";
import { toast as myToast } from "react-toastify";
import { capitalEachLetter } from "utils/capitalEachLetter";
import CollectionDiscriptionModal from "features/collection/components/Modal/CollectionDiscriptionModal";
import { Message } from "@mui/icons-material";

const StyledButton = styled(Button)(({ variant, disabled }) => ({
  width: variant === "save" ? "168px" : variant === "cancel" ? "110px" : variant === "save-rights" ? "150px" : "160px",
  fontFamily: variant === "save-rights" ? "poppins" : "karla",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "21.04px",
  height: "41px",
  borderRadius: "5px",
  color: disabled ? "#B0B0B0" : variant === "save" ? "#50C878" : variant === "cancel" ? "#E77B7B" : variant === "save-rights" ? "#50C878" : "#FFF",

  border: `1px solid ${disabled ? "#B0B0B0" : variant === "save" ? "#50C878" : variant === "cancel" ? "#E77B7B" : variant === "save-rights" ? "#50C878" : "#fff"}`,
  textTransform: "none",
  backgroundColor: "transparent",

  "&:hover": {
    backgroundColor: disabled ? "transparent" : variant === "save" ? "#50C878" : variant === "cancel" ? "#E77B7B" : variant === "save-rights" ? "#50C878" : "#fff",
    color: disabled ? "#B0B0B0" : variant === "save" ? "#fff" : variant === "cancel" ? "#fff" : variant === "save-rights" ? "#fff" : "#000",
  },
}));

const StyledTableContainer = styled(TableContainer)(({}) => ({
  backgroundColor: "transparent",
  maxHeight: "76.5vh",
  position: "relative",
  marginBottom: "0px",
  // paddingBottom: "5px",
  border: "1px solid #fff",
  // Hide scrollbars for Webkit browsers
  "&::-webkit-scrollbar": {
    width: "0px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "transparent",
  },

  // Hide scrollbars for Firefox
  scrollbarWidth: "none", // For Firefox
}));

const StyledTable = styled(Table)(({}) => ({
  "& thead": {
    "& th": {
      lineHeight: "22px",
      color: "#FFFFFF",
      textAlign: "center",
      fontFamily: "Poppins",
      fontWeight: 400,
      fontSize: "14px",
    },
  },
  "& tbody": {
    "& td": {
      fontFamily: "Poppins",
      fontWeight: 400,
      fontSize: "18px",
      lineHeight: "22px",
      color: "#FFFFFF",
      textAlign: "center",
      border: "1px solid #fff",
      padding: 0,
      padding: "10px",
      "&:first-child": {
        borderLeft: "none",
      },
      "&:last-child": {
        borderRight: "none",
      },
    },
  },
  "& th": {
    backgroundColor: "#444444",
    height: "50px !important",
  },
  "& tfoot": {
    "& td": {
      fontFamily: "Poppins",
      fontWeight: 400,
      fontSize: "18px",
      lineHeight: "22px",
      textAlign: "center",
    },
  },
}));

const StyledTableHeaderCell = styled(TableCell)(({}) => ({
  fontFamily: "Poppins !important",
  position: "sticky",
  top: 0,
  padding: "10px",
  paddingLeft: "15px",
  paddingRight: "15px",
  border: "none",
  zIndex: 1,

  // "&:first-child": {
  //   borderTopLeftRadius: "5px",
  //   borderBottomLeftRadius: "5px",
  // },
  // "&:last-child": {
  //   borderTopRightRadius: "5px",
  //   borderBottomRightRadius: "5px",
  // },
}));

const StyledTableBodyCell = styled(TableCell)(({ color, width }) => ({
  fontFamily: "Karla !important",
  padding: "10px",
  paddingLeft: "5px",
  paddingRight: "5px",
  border: "none",
  color: `${color} !important`,
  width: width || null,
  maxWidth: width || null,
  overflow: width ? "hidden" : null,
  textOverflow: width ? "ellipsis" : null,
  whiteSpace: width ? "normal" : null,
  "&:first-child": {},
  "&:last-child": {},
}));

const StyledTableBodyCellRemark = styled(TableCell)`
  width: 200px; /* or any specific width */
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

const StyledTableRow = styled(TableRow)(({}) => ({
  "& td": {
    // padding: "20px",
    // paddingTop: "0px",
  },
  "&:first-of-type td": {
    // padding: "20px",
    // paddingTop: "15px",
  },
}));

const TableSkeleton = ({ columns, rows = 5 }) => (
  <>
    {Array.from(new Array(rows)).map((_, rowIndex) => (
      <StyledTableRow key={`skeleton-${rowIndex}`}>
        {Array.from(new Array(columns)).map((_, cellIndex) => (
          <StyledTableBodyCell key={`skeleton-cell-${cellIndex}`}>
            <Skeleton variant="text" sx={{ fontSize: "1rem" }} animation="wave" width={"100%"} height={"90%"} />
          </StyledTableBodyCell>
        ))}
      </StyledTableRow>
    ))}
  </>
);

const columns = [
  { id: 1, label: "Design No." },
  { id: 2, label: "Diamond Qty" },
  { id: 3, label: "Diamond W/P" },
  { id: 4, label: "Metal Qty & Color" },
  { id: 5, label: "Gold Weight N/G" },
  { id: 6, label: "Size" },
  { id: 7, label: "labour" },
  { id: 8, label: "Remark" },
  { id: 9, label: "View Design" },
];

const OrderDetails = () => {
  const { orderId } = useParams();
  const [imageModal, setImageModal] = React.useState({ isOpen: false, imgUrl: null, designNo: null });
  const [openRemarkModel, setOpenRemarkModel] = useState(false);
  const [remarkData, setRemarkData] = useState({});
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, isError, error } = useOrdersDetails({
    limit: 10,
    orderId: orderId,
  });
  console.log("Data Order Data --->>", data?.pages[0]?.orderData);

  const formattedOrderData = useCallback(
    (data, key) => {
      const newObjectOrder = {
        str: data
          ? `${data?.pages[0]?.orderDate}, ${data?.pages[0]?.client?.clientNumber} (${capitalEachLetter(data?.pages[0]?.client?.clientName)}), #${data?.pages[0]?.catalog?.catalogNumber} (${capitalEachLetter(data?.pages[0]?.catalog?.catalogName)})`
          : null,
        totalGold: `${parseFloat(data?.pages[0]?.totalGoldNetWeight || 0).toFixed(2)}/${parseFloat(data?.pages[0]?.totalGoldGrossWeight || 0).toFixed(2)}`,
        totalDiamond: `${parseFloat(data?.pages[0]?.totalDiaWeight || 0).toFixed(2)}/${data?.pages[0]?.totalDiaPcs || 0}`,
        orderNumber: data?.pages[0]?.orderNumber,
      };

      return newObjectOrder[key];
    },
    [data?.pages[0]?.orderDate]
  );

  const handleOpenImageModal = React.useCallback((imgUrl, designNo) => {
    setImageModal({ isOpen: true, imgUrl, designNo });
  }, []);

  const handleCloseImageModal = React.useCallback(() => {
    setImageModal({ isOpen: false, imgUrl: null, designNo: null });
  }, []);

  const handleOrderDownloadDetails = debounce(async (orderId) => {
    try {
      const response = await apiClient.get(`/api/v3/clinet/order/excel/${orderId}`, { responseType: "arraybuffer" });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Order-(${formattedOrderData(data, "orderNumber") || "order"}).xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      myToast.success("Download Excel successfully!", {
        position: "top-right",
        autoClose: 1500,
      });
    } catch (error) {
      myToast.error(`Error: ${"Failed to Download"}`, {
        position: "top-right",
        autoClose: 1500,
      });
    }
  }, 1000);

  const formatMessage = (message) => {
    return message.length > 40 ? message.slice(0, 40) + "..." : message;
  };

  const handleRemarkClick = (data) => {
    if (data.message) {
      setOpenRemarkModel(true);
      setRemarkData({
        styleNumber: data?.styleNumber,
        message: data?.message,
      });
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          border: "1px solid red",
          height: "42px",
          alignItems: "center",
          margin: "20px 0 20px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <p
          style={{
            fontFamily: "Poppins",
            fontWeight: 400,
            fontSize: "18px",
            lineHeight: "36px",
            width: "80%",
          }}
        >
          {formattedOrderData(data, "str") ?? <Skeleton variant="text" sx={{ fontSize: "1rem" }} animation="wave" width={"60%"} height={"90%"} />}
        </p>
        <StyledButton onClick={() => handleOrderDownloadDetails(orderId)}>Download Order</StyledButton>
      </div>

      <StyledTableContainer>
        <StyledTable>
          {/* header */}
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <StyledTableHeaderCell key={column.id}>{column.label}</StyledTableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          {/* end header */}

          <TableBody>
            {isLoading ? (
              <TableSkeleton columns={9} rows={10} />
            ) : (
              data?.pages?.map((page) =>
                page?.orderData?.map((order, _ind) => {
                  const { _id, styleNumber, diaQty, diaWeight, diaPcs, goldQty, goldColor, netWeight, grossWeight, makeType, imageUrl, message, size } = order;
                  return (
                    <StyledTableRow key={_id}>
                      <StyledTableBodyCell>{styleNumber}</StyledTableBodyCell>
                      <StyledTableBodyCell>{diaQty}</StyledTableBodyCell>
                      <StyledTableBodyCell>
                        {diaWeight}/{diaPcs}
                      </StyledTableBodyCell>
                      <StyledTableBodyCell>
                        {goldQty}-{goldColor}
                      </StyledTableBodyCell>
                      <StyledTableBodyCell>
                        {netWeight}/{grossWeight}
                      </StyledTableBodyCell>
                      <StyledTableBodyCell>{size ? size : "-"}</StyledTableBodyCell>
                      <StyledTableBodyCell>{makeType}</StyledTableBodyCell>
                      <StyledTableBodyCell width="277px" onClick={() => handleRemarkClick({ styleNumber, message })}>
                        {message ? formatMessage(message) : <span style={{ color: "#8B8B8B" }}>No Data Available</span>}
                      </StyledTableBodyCell>
                      <StyledTableBodyCell>
                        <IconButton onClick={() => handleOpenImageModal(imageUrl, styleNumber)}>{getIcon("eye")}</IconButton>
                      </StyledTableBodyCell>
                    </StyledTableRow>
                  );
                })
              )
            )}
            {isFetchingNextPage && <TableSkeleton columns={8} rows={5} />}
          </TableBody>

          <tfoot>
            <StyledTableRow>
              <StyledTableBodyCell>Grand Total</StyledTableBodyCell>
              <StyledTableBodyCell></StyledTableBodyCell>
              <StyledTableBodyCell>{formattedOrderData(data, "totalDiamond")}</StyledTableBodyCell>
              <StyledTableBodyCell></StyledTableBodyCell>
              <StyledTableBodyCell>{formattedOrderData(data, "totalGold")}</StyledTableBodyCell>
              <StyledTableBodyCell></StyledTableBodyCell>
              <StyledTableBodyCell></StyledTableBodyCell>
              {/* <StyledTableBodyCell></StyledTableBodyCell> */}
            </StyledTableRow>
          </tfoot>
        </StyledTable>
      </StyledTableContainer>
      <CollectionDiscriptionModal open={openRemarkModel} handleClose={() => setOpenRemarkModel(false)} data={remarkData} />
      <ImageModal open={imageModal.isOpen} handleClose={handleCloseImageModal} data={imageModal} />
    </div>
  );
};

export default OrderDetails;
